import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

const COLORS = ["#ffd76a", "#ffb020", "#ff7a18", "#ff4d4d", "#ffe8a3", "#fff6d6", "#ff9f43"];

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function Diya({ className }) {
  return (
    <span className={`diwali-lamp ${className || ""}`} aria-hidden>
      <span className="diwali-lamp__flame" />
      <span className="diwali-lamp__bowl" />
    </span>
  );
}

export default function DiwaliOfferOverlay({ onExplore }) {
  const titleId = useId();
  const descId = useId();
  const canvasRef = useRef(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const particlesRef = useRef([]);
  const rocketsRef = useRef([]);
  const rafRef = useRef(0);
  const lastClick = useRef(0);
  const [open, setOpen] = useState(true);
  const reducedRef = useRef(prefersReducedMotion());

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const explode = useCallback((x, y, count = 42) => {
    const particles = particlesRef.current;
    for (let i = 0; i < count; i += 1) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.35;
      const speed = 2.2 + Math.random() * 4.8;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        decay: 0.008 + Math.random() * 0.01,
        size: 1.8 + Math.random() * 2.8,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      });
    }
  }, []);

  const launchRocket = useCallback((fromX) => {
    const h = window.innerHeight;
    rocketsRef.current.push({
      x: fromX,
      y: h + 10,
      vx: (Math.random() - 0.5) * 0.8,
      vy: -(7.2 + Math.random() * 2.4),
      targetY: 70 + Math.random() * (h * 0.38),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    });
  }, []);

  const startShow = useCallback(() => {
    if (reducedRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();

    const w = window.innerWidth;
    [0.1, 0.28, 0.5, 0.72, 0.9].forEach((p, i) => {
      window.setTimeout(() => launchRocket(w * p), i * 220);
    });
    const extras = window.setInterval(() => {
      launchRocket(40 + Math.random() * (window.innerWidth - 80));
    }, 420);
    window.setTimeout(() => window.clearInterval(extras), 3200);

    const tick = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.globalCompositeOperation = "lighter";

      const rockets = rocketsRef.current;
      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.06;
        ctx.fillStyle = r.color;
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(255, 236, 180, 0.45)";
        ctx.beginPath();
        ctx.arc(r.x, r.y + 8, 1.4, 0, Math.PI * 2);
        ctx.fill();
        if (r.y <= r.targetY || r.vy >= -0.4) {
          explode(r.x, r.y, 48);
          rockets.splice(i, 1);
        }
      }

      const particles = particlesRef.current;
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.04;
        p.vx *= 0.992;
        p.life -= p.decay;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 16;
        ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      rafRef.current = window.requestAnimationFrame(tick);
    };
    window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(tick);
  }, [explode, launchRocket]);

  useEffect(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    window.setTimeout(() => closeRef.current?.focus(), 50);
    startShow();
    const onResize = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.cancelAnimationFrame(rafRef.current);
    };
  }, [open, close, startShow]);

  useEffect(() => {
    if (!open) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;
    const onTab = (e) => {
      if (e.key !== "Tab") return;
      const focusable = node.querySelectorAll("button");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener("keydown", onTab);
    return () => node.removeEventListener("keydown", onTab);
  }, [open]);

  const burstAtEvent = (e) => {
    if (reducedRef.current || !open) return;
    const now = Date.now();
    if (now - lastClick.current < 220) return;
    lastClick.current = now;
    explode(e.clientX, e.clientY, 36);
  };

  const handleExplore = () => {
    explode(window.innerWidth / 2, window.innerHeight * 0.38, 64);
    window.setTimeout(close, 520);
    onExplore?.();
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="diwali-overlay" role="presentation">
      <canvas ref={canvasRef} className="diwali-overlay__canvas" aria-hidden />
      <button
        type="button"
        className="diwali-overlay__backdrop"
        aria-label="Close Diwali offer"
        onClick={(e) => {
          burstAtEvent(e);
          close();
        }}
      />
      <div
        ref={dialogRef}
        className="diwali-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        onClick={burstAtEvent}
      >
        <div className="diwali-modal__frame" aria-hidden />
        <Diya className="diwali-diya--tl" />
        <Diya className="diwali-diya--tr" />
        <Diya className="diwali-diya--bl" />
        <Diya className="diwali-diya--br" />

        <button
          ref={closeRef}
          type="button"
          className="diwali-modal__close"
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
        >
          ×
        </button>

        <div className="diwali-modal__lamps" aria-hidden>
          <Diya />
          <Diya />
          <Diya />
          <Diya />
          <Diya />
        </div>

        <p className="diwali-modal__greeting" id={titleId}>
          Happy Diwali
        </p>
        <p className="diwali-modal__kicker">Diwali Special Offer</p>
        <h2 className="diwali-modal__offer">₹0 Registration Fee</h2>
        <p className="diwali-modal__text" id={descId}>
          Register your company for free. Pay <strong>₹100 for Super Admin verification</strong>.
          Every user activation is ₹100 — there is no free user.
        </p>
        <p className="diwali-modal__hint">Click anywhere for fireworks</p>
        <button
          type="button"
          className="diwali-modal__cta"
          onClick={(e) => {
            e.stopPropagation();
            handleExplore();
          }}
        >
          Explore Plans
        </button>
      </div>
    </div>,
    document.body
  );
}
